/**
 * Direct Supabase data access for the SaaS SPA.
 *
 * All queries go through the user's JWT via the Supabase client,
 * so Row Level Security enforces access control automatically.
 * No API server needed for CRUD operations.
 */

import type {SupabaseClient} from '@supabase/supabase-js';
import type {AudioClipConfig, AudioTrackConfig} from '@cascade/core';

const AUDIO_BUCKET = 'project-audio';

// ---------------------------------------------------------------------------
// Types (mirrors the Supabase schema)
// ---------------------------------------------------------------------------

export interface Project {
  id: string;
  user_id: string;
  name: string;
  settings: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface Scene {
  id: string;
  project_id: string;
  name: string;
  code: string;
  order_index: number;
  duration_frames: number;
  created_at: string;
  updated_at: string;
}

// ---------------------------------------------------------------------------
// Projects
// ---------------------------------------------------------------------------

export async function listProjects(sb: SupabaseClient): Promise<Project[]> {
  const {data, error} = await sb
    .from('projects')
    .select('*')
    .order('updated_at', {ascending: false});
  if (error) throw new Error(error.message);
  return data ?? [];
}

export async function getProject(
  sb: SupabaseClient,
  id: string,
): Promise<Project> {
  const {data, error} = await sb
    .from('projects')
    .select('*')
    .eq('id', id)
    .single();
  if (error) throw new Error(error.message);
  return data;
}

export async function createProject(
  sb: SupabaseClient,
  name: string,
  settings: Record<string, unknown> = {},
): Promise<Project> {
  const {data: {user}} = await sb.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const {data, error} = await sb
    .from('projects')
    .insert({name, settings, user_id: user.id})
    .select()
    .single();
  if (error) throw new Error(error.message);
  return data;
}

export async function updateProject(
  sb: SupabaseClient,
  id: string,
  updates: {name?: string; settings?: Record<string, unknown>},
): Promise<Project> {
  const {data, error} = await sb
    .from('projects')
    .update(updates)
    .eq('id', id)
    .select()
    .single();
  if (error) throw new Error(error.message);
  return data;
}

export async function deleteProject(
  sb: SupabaseClient,
  id: string,
): Promise<void> {
  const {error} = await sb.from('projects').delete().eq('id', id);
  if (error) throw new Error(error.message);
}

// ---------------------------------------------------------------------------
// Scenes
// ---------------------------------------------------------------------------

export async function listScenes(
  sb: SupabaseClient,
  projectId: string,
): Promise<Scene[]> {
  const {data, error} = await sb
    .from('scenes')
    .select('*')
    .eq('project_id', projectId)
    .order('order_index', {ascending: true});
  if (error) throw new Error(error.message);
  return data ?? [];
}

export async function createScene(
  sb: SupabaseClient,
  params: {
    project_id: string;
    name: string;
    code?: string;
    order_index?: number;
    duration_frames?: number;
  },
): Promise<Scene> {
  const {data, error} = await sb
    .from('scenes')
    .insert({
      project_id: params.project_id,
      name: params.name,
      code: params.code ?? '',
      order_index: params.order_index ?? 0,
      duration_frames: params.duration_frames ?? 150,
    })
    .select()
    .single();
  if (error) throw new Error(error.message);
  return data;
}

export async function updateScene(
  sb: SupabaseClient,
  id: string,
  updates: {name?: string; code?: string; order_index?: number; duration_frames?: number},
): Promise<Scene> {
  const {data, error} = await sb
    .from('scenes')
    .update(updates)
    .eq('id', id)
    .select()
    .single();
  if (error) throw new Error(error.message);
  return data;
}

export async function deleteScene(
  sb: SupabaseClient,
  id: string,
): Promise<void> {
  const {error} = await sb.from('scenes').delete().eq('id', id);
  if (error) throw new Error(error.message);
}

// ---------------------------------------------------------------------------
// Audio Storage
// ---------------------------------------------------------------------------

/** Upload an audio blob to Supabase Storage, return the permanent public URL. */
export async function uploadAudio(
  sb: SupabaseClient,
  projectId: string,
  filename: string,
  blob: Blob,
): Promise<string> {
  const sanitized = filename.replace(/[^a-zA-Z0-9._-]/g, '_');
  const path = `${projectId}/${Date.now()}-${sanitized}`;

  const {error} = await sb.storage
    .from(AUDIO_BUCKET)
    .upload(path, blob, {contentType: blob.type || 'audio/mpeg', upsert: false});
  if (error) throw new Error(`Audio upload failed: ${error.message}`);

  const {data} = sb.storage.from(AUDIO_BUCKET).getPublicUrl(path);
  return data.publicUrl;
}

/** Save audio clips and tracks to the project's settings JSONB column. */
export async function saveAudioState(
  sb: SupabaseClient,
  projectId: string,
  clips: AudioClipConfig[],
  tracks: AudioTrackConfig[],
  existingSettings: Record<string, unknown>,
): Promise<void> {
  await updateProject(sb, projectId, {
    settings: {
      ...existingSettings,
      audioClips: clips,
      audioTracks: tracks,
    },
  });
}

// ---------------------------------------------------------------------------
// Audio Analysis Persistence
// ---------------------------------------------------------------------------

/**
 * Save an audio analysis result to the project's settings JSONB column.
 * Keyed by audio source URL so each clip's analysis can be looked up independently.
 */
export async function saveAudioAnalysis(
  sb: SupabaseClient,
  projectId: string,
  audioSrc: string,
  analysis: Record<string, unknown>,
): Promise<void> {
  // Read current settings to merge (avoid overwriting other fields)
  const project = await getProject(sb, projectId);
  const settings = project.settings ?? {};
  const existing = (settings.audioAnalyses as Record<string, unknown>) ?? {};

  await updateProject(sb, projectId, {
    settings: {
      ...settings,
      audioAnalyses: {
        ...existing,
        [audioSrc]: analysis,
      },
    },
  });
}

/**
 * Extract cached audio analyses from project settings.
 * Returns a map of audioSrc -> AnalyzedAudio objects.
 */
export function getAudioAnalyses(
  settings: Record<string, unknown>,
): Record<string, unknown> {
  return (settings?.audioAnalyses as Record<string, unknown>) ?? {};
}
